import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import ImageUpload from "@/components/ImageUpload";

const FIELDS = [
  { name: "about_story", label: "About — PTG Story", type: "textarea" },
  { name: "about_founder", label: "About — Founder Information", type: "textarea" },
  { name: "address", label: "Address", type: "text" },
  { name: "phone", label: "Phone Number", type: "text" },
  { name: "whatsapp", label: "WhatsApp Number (with country code, digits only)", type: "text", hint: "e.g. 919876543210" },
  { name: "email", label: "Email", type: "text" },
  { name: "hours", label: "Teaching / Office Hours", type: "text" },
  { name: "instagram_url", label: "Instagram URL", type: "text" },
  { name: "youtube_url", label: "YouTube URL", type: "text" },
  { name: "maps_embed", label: "Google Maps Embed URL", type: "textarea", hint: "Google Maps → Share → Embed a map → copy the src URL only" },
  { name: "maps_link", label: "Google Maps Link (opens on click)", type: "text", hint: "Google Maps → Share → copy link (e.g. https://maps.app.goo.gl/...)" },
];

export default function SitePanel() {
  const { site, refresh } = useSite();
  const [form, setForm] = useState(site);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/site").then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        about_story: form.about_story || "", about_founder: form.about_founder || "",
        address: form.address || "", phone: form.phone || "", whatsapp: form.whatsapp || "",
        email: form.email || "", hours: form.hours || "", instagram_url: form.instagram_url || "",
        youtube_url: form.youtube_url || "", maps_embed: form.maps_embed || "",
        maps_link: form.maps_link || "",
        community_photo: form.community_photo || "",
        founder_photo: form.founder_photo || "",
      };
      await api.put("/admin/site", payload);
      await refresh();
      toast.success("Contact & site info saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="panel-site" className="max-w-2xl">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Contact & Site Information</h2>
      <p className="text-sm text-slate-500 mb-6">Update details shown across the website (About, Contact, footer, WhatsApp button, social links).</p>
      <div className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <ImageUpload label="Homepage Community Photo (large group photo)" value={form.community_photo} onChange={(v) => setForm((f) => ({ ...f, community_photo: v }))} />
          <p className="text-xs text-slate-400 mt-1">Shown in the "More Than Tuition. A Learning Community." section on the homepage.</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <ImageUpload label="About Page — Founder Photo (Krunal & Pooja)" value={form.founder_photo} onChange={(v) => setForm((f) => ({ ...f, founder_photo: v }))} />
          <p className="text-xs text-slate-400 mt-1">Shown in the founder section on the About Us page.</p>
        </div>
        {FIELDS.map((fd) => (
          <div key={fd.name}>
            <Label className="mb-1.5 block text-sm font-semibold text-slate-700">{fd.label}</Label>
            {fd.type === "textarea" ? (
              <Textarea value={form[fd.name] || ""} onChange={(e) => setForm((f) => ({ ...f, [fd.name]: e.target.value }))} rows={3} data-testid={`site-field-${fd.name}`} />
            ) : (
              <Input value={form[fd.name] || ""} onChange={(e) => setForm((f) => ({ ...f, [fd.name]: e.target.value }))} data-testid={`site-field-${fd.name}`} />
            )}
            {fd.hint && <p className="text-xs text-slate-400 mt-1">{fd.hint}</p>}
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-950" data-testid="site-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save Changes</>}
        </Button>
      </div>
    </div>
  );
}
